import machine from '@app/machine.js';
import config from '@app/config.js';
import timer from '@components/timer/timer.js';
import storage from './storage.js';
import { copyEmptyMatrix } from '@utils/traverseMatrices.js';

/** @type {NonogramData[]} */
const NONOGRAMS = config.NONOGRAMS;

/** @typedef {(import('./components/timer/timer.js').Timer)} Timer */

/** @typedef {(import('./storage.js').Storage)} Storage */

class Controller {
  /** @type {StateMachine} */
  machine;
  /** @type {Timer} */
  timer;

  /** @type {Storage} */
  storage;
  /** @type {NonogramData[]} */
  nonograms;
  /** @type {NonogramData} */
  currentNonogram;
  /** @type {NonogramData['solution']} */
  solving;
  constructor() {
    this.appMachine = machine;
    this.timer = timer;
    this.storage = storage;
    this.nonograms = NONOGRAMS;
    this.currentNonogram = this.nonograms[0];
    this.solving = copyEmptyMatrix(this.currentNonogram.solution);
  }

  /**
   * Initiates the app controller.
   * @returns {void}
   */
  init() {
    this.applyPreferences();
    this.setListeners();

    window.dispatchEvent(
      new CustomEvent('custom:renderNonogram', {
        detail: { nonogram: this.currentNonogram, isGameLoaded: false },
      })
    );

    this.logSolution();
  }

  /**
   * Initiates opening modal with nonogram options.
   * @param {string} returnEvent
   * @returns {void}
   */
  showChangeNonogram(returnEvent) {
    window.dispatchEvent(
      new CustomEvent('custom:showChange', {
        detail: { currentId: this.currentNonogram.id },
      })
    );

    this.appMachine.transition(this.appMachine.value, returnEvent);
  }

  /**
   * Initiates opening modal with game results.
   * @param {string} returnEvent
   * @returns {void}
   */
  showResults(returnEvent) {
    let results = /** @type {Result[]} */ (this.storage.getItem('results'));
    if (!results) results = [];

    results = results.toSorted((a, b) => a.time - b.time);

    window.dispatchEvent(
      new CustomEvent('custom:showResults', {
        detail: { results },
      })
    );

    this.appMachine.transition(this.appMachine.value, returnEvent);
  }

  /**
   * Renders changed nonogram.
   * @param {CustomEvent<{id: number}>} e
   * @returns {void}
   */
  applyNonogramChange = (e) => {
    const { id } = e.detail;
    const newNonogram = NONOGRAMS.find((item) => item.id === id);
    this.currentNonogram = newNonogram || NONOGRAMS[0];
    this.solving = copyEmptyMatrix(this.currentNonogram.solution);
    this.timer.resetTimer();

    window.dispatchEvent(
      new CustomEvent('custom:renderNonogram', {
        detail: { nonogram: this.currentNonogram, isGameLoaded: false },
      })
    );

    this.logSolution();

    this.appMachine.transition(this.appMachine.value, 'idle');
  };

  /**
   * Displays nonogram's solution on the game field.
   * @returns {void}
   */
  showSolution() {
    this.timer.resetTimer();

    window.dispatchEvent(
      new CustomEvent('custom:showSolution', {
        detail: { solution: this.currentNonogram.solution },
      })
    );
  }

  /**
   * Saves current solving matrix to local storage.
   * @param {string} returnEvent
   * @returns {void}
   */
  saveCurrentSolving(returnEvent) {
    /** @type {Save} */
    const save = {
      id: this.currentNonogram.id,
      solving: this.solving,
      time: this.timer.time,
    };

    this.storage.setItem('save', save);
    this.appMachine.transition(this.appMachine.value, returnEvent);
  }

  /**
   * Displays disallow save message.
   * @param {string} returnEvent
   * @returns {void}
   */
  showDisallowSave(returnEvent) {
    window.dispatchEvent(new Event('custom:showDisallowSave'));
    this.appMachine.transition(this.appMachine.value, returnEvent);
  }

  /**
   * Apply changes according to saved nonogram.
   * @param {string} returnEvent
   * @returns {void}
   */
  continueWithLoadedSolving(returnEvent) {
    const save = this.loadSavedNonogram();

    if (!save) {
      window.dispatchEvent(new Event('custom:showNoSaveMessage'));
      this.appMachine.transition(this.appMachine.value, returnEvent);
      return;
    }

    this.timer.resetTimer(save.time);

    window.dispatchEvent(
      new CustomEvent('custom:renderNonogram', {
        detail: {
          nonogram: this.currentNonogram,
          isGameLoaded: true,
          solving: this.solving,
        },
      })
    );

    this.logSolution();

    this.appMachine.transition(this.appMachine.value, 'idle');
  }

  /**
   * Load saved nonogram.
   * @returns {Save | undefined}
   * @private
   */
  loadSavedNonogram() {
    const save = /** @type {Save} */ (this.storage.getItem('save'));

    if (!save) return;

    this.solving = save.solving;
    this.currentNonogram = /** @type {NonogramData} */ (
      NONOGRAMS.find((item) => item.id === save.id)
    );

    return save;
  }

  /**
   * Apply random nonogram/
   * @returns {void}
   */
  getRandomNonogram() {
    /**
     * Returns a random item from array.
     * @template T
     * @param {T[]} array
     * @returns {T}
     */
    function getRandom(array) {
      const index = Math.floor(Math.random() * array.length);
      return array[index];
    }

    let randomNonogram;

    do {
      randomNonogram = getRandom(NONOGRAMS);
    } while (randomNonogram.id === this.currentNonogram.id);

    this.currentNonogram = randomNonogram;
    this.solving = copyEmptyMatrix(this.currentNonogram.solution);
    this.timer.resetTimer();
    window.dispatchEvent(
      new CustomEvent('custom:renderNonogram', {
        detail: { nonogram: this.currentNonogram, isGameLoaded: false },
      })
    );

    this.logSolution();

    this.appMachine.transition(this.appMachine.value, 'idle');
  }

  /**
   * Apply sound and theme user preferences.
   * @returns {void}
   * @private
   */
  applyPreferences() {
    const { isSound, isDarkTheme } = this.storage.getPreferences();

    window.dispatchEvent(
      new CustomEvent('custom:applySound', { detail: { isSound } })
    );

    window.dispatchEvent(
      new CustomEvent('custom:applyTheme', { detail: { isDarkTheme } })
    );
  }

  /**
   * Saves the won game to local storage.
   * @returns {void}
   * @private
   */
  saveWinResult() {
    /**
     * @param {number} timeSec
     * @returns {string}
     */
    function formatTime(timeSec) {
      const min = Math.floor(timeSec / 60);
      const sec = timeSec % 60;
      return `${min}:${sec < 10 ? '0' : ''}${sec}`;
    }

    const formattedTime = formatTime(this.timer.time);
    this.storage.storeResult({
      name: this.currentNonogram.name,
      difficulty: this.currentNonogram.difficulty,
      time: this.timer.time,
      formattedTime,
    });
  }

  /**
   * Makes several actions to implement win condition.
   * @returns {void}
   */
  processWin() {
    window.dispatchEvent(new CustomEvent('custom:win'));
  }

  /**
   * Resets current nonogram.
   * @returns {void}
   */
  resetNonogram() {
    this.solving = copyEmptyMatrix(this.currentNonogram.solution);
    this.timer.resetTimer();
    window.dispatchEvent(new CustomEvent('custom:resetNonogram'));

    this.appMachine.transition('reset', 'idle');
  }

  /**
   * Triggers state transition.
   * @param {CustomEvent<MachineCustomEventDetail>} e
   * @returns {void}
   * @private
   */
  handleStateChange = (e) => {
    const { action, payload } = e.detail;
    const currentState = this.appMachine.value;
    this.appMachine.transition(currentState, action, {
      transitionFromState: currentState,
      ...payload,
    });
  };

  /**
   * Handles nonogram clicks.
   * @param {{x: number, y: number, value: number}} cell
   * @returns {void}
   */
  processNonogramClicks(cell) {
    this.solving[cell.y][cell.x] = cell.value;

    const { solution } = this.currentNonogram;
    for (let i = 0; i < solution.length; i++) {
      for (let j = 0; j < solution[i].length; j++) {
        const userCell = this.solving[i][j];
        const solutionCell = solution[i][j];
        if (
          (userCell === 1 || solutionCell === 1) &&
          userCell !== solutionCell
        ) {
          this.appMachine.transition(this.appMachine.value, 'game');
          return;
        }
      }
    }

    window.dispatchEvent(
      new CustomEvent('custom:showWinMessage', {
        detail: { time: this.timer.time },
      })
    );

    this.saveWinResult();
    this.timer.pauseTimer();
    this.appMachine.transition(this.appMachine.value, 'win');
  }

  /**
   * Sets event listeners.
   * @returns {void}
   * @private
   */
  setListeners() {
    window.addEventListener(
      'custom:changestate',
      /** @type {EventListener} */ (this.handleStateChange)
    );

    window.addEventListener(
      'custom:changeNonogram',
      /** @type {EventListener} */ (this.applyNonogramChange)
    );
  }

  logSolution() {
    let resultString = '';
    this.currentNonogram.solution.forEach((row) => {
      let rowString = '';
      row.forEach((cell) => {
        rowString += cell ? '⬛' : '⬜';
      });
      resultString += '\n' + rowString;
    });

    console.log(`%c${resultString}`, 'background: silver');
  }
}

export default new Controller();
